import { Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

import { NotificationsContext } from "@contexts/Notifications/context";
import { NotificationItem } from "./NotificationItem";
import * as S from "./styles";

interface Props {
  onClose: () => void;
}

export function NotificationsPopover({ onClose }: Props) {
  const {
    notifications,
    loadNotifications,
    markAllAsViewed,
    refreshSummary,
    acceptNotification,
  } = useContext(NotificationsContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptingIds, setAcceptingIds] = useState<string[]>([]);
  let content: ReactNode;

  const handleAccept = useCallback(
    async (id: string) => {
      setAcceptingIds((prev) => [...prev, id]);

      try {
        await acceptNotification(id);
        await refreshSummary();
      } catch (requestError) {
        console.error("Erro ao aceitar notificação", requestError);
      } finally {
        setAcceptingIds((prev) => prev.filter((itemId) => itemId !== id));
      }
    },
    [acceptNotification, refreshSummary],
  );

  useEffect(() => {
    const initializePopover = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await loadNotifications();
        const pendingViewIds = data
          .filter((notification) => !notification.visualizada)
          .map((notification) => notification.id);

        if (pendingViewIds.length > 0) {
          await markAllAsViewed(pendingViewIds);
        }

        await refreshSummary();
      } catch (requestError) {
        console.error("Erro ao carregar notificações", requestError);
        setError("Não foi possível carregar as notificações.");
      } finally {
        setLoading(false);
      }
    };

    void initializePopover();
  }, [loadNotifications, markAllAsViewed, refreshSummary]);

  if (loading) {
    content = (
      <Typography.Text type="secondary">
        Carregando notificações...
      </Typography.Text>
    );
  } else if (error) {
    content = <Typography.Text type="danger">{error}</Typography.Text>;
  } else if (notifications.length === 0) {
    content = (
      <Typography.Text type="secondary">
        Nenhuma notificação no momento!
      </Typography.Text>
    );
  } else {
    content = notifications.map((notification) => (
      <NotificationItem
        key={notification.id}
        notification={notification}
        onAccept={handleAccept}
        isAccepting={acceptingIds.includes(notification.id)}
      />
    ));
  }

  return (
    <S.PopoverContent>
      <S.Header>
        <S.TitleWrapper>
          <CloseOutlined onClick={onClose} style={{ cursor: "pointer" }} />

          <Typography.Title level={5} style={{ margin: 0 }}>
            Notificações
          </Typography.Title>
        </S.TitleWrapper>
      </S.Header>

      {content}
    </S.PopoverContent>
  );
}
